<template>
    <div v-if="show">
        <div
            v-if="header != null"
            class="d-flex align-items-center mb-2">
            <div class="fw-bold flex-grow-1">
                {{ header }}
            </div>
            <div v-if="showExpandLink">
                <pc-button
                    link
                    class="p-0"
                    v-on:click.stop="toggleExpanded">
                    <div class="text-end">
                        {{ expandLinkText }}
                    </div>
                </pc-button>
            </div>
        </div>
        <div v-if="empty">
            <slot name="empty">
                <div class="ms-3 fst-italic">
                    {{ emptyMessageDisplay }}
                </div>
            </slot>
        </div>
        <div v-else>
            <div>
                <div v-if="useBullets">
                    <!-- suppress usual bottom padding of ul in this case-->
                    <ul class="mb-0">
                        <li
                            v-for="(item, index) in initialItemList"
                            v-bind:key="getItemKey(item)">
                            <slot
                                name="index"
                                v-bind:value="index + 1" />
                            <slot
                                v-bind="item"
                                name="content"
                                v-bind:display-index="index + 1">
                                {{ item }}
                            </slot>
                        </li>
                    </ul>
                </div>
                <div v-else>
                    <div
                        v-for="(item, index) in initialItemList"
                        v-bind:key="getItemKey(item)">
                        <slot
                            name="index"
                            v-bind:value="index + 1" />
                        <slot
                            v-bind="item"
                            name="content"
                            v-bind:display-index="index + 1">
                            {{ item }}
                        </slot>
                    </div>
                </div>
            </div>
            <pc-transition transition="slide">
                <div
                    v-if="isExpanded"
                    data-test-role="expand-list-expanded-contents">
                    <div v-if="useBullets">
                        <!-- suppress usual bottom padding of ul in this case-->
                        <ul class="mb-0">
                            <li
                                v-for="(item, index) in moreItemList"
                                v-bind:key="getItemKey(item)">
                                <slot
                                    name="index"
                                    v-bind:value="index + initialItemLimit + 1" />
                                <slot
                                    v-bind="item"
                                    name="content"
                                    v-bind:display-index="index + initialItemLimit + 1">
                                    {{ item }}
                                </slot>
                            </li>
                        </ul>
                    </div>
                    <div v-else>
                        <div
                            v-for="(item, index) in moreItemList"
                            v-bind:key="getItemKey(item)">
                            <slot
                                name="index"
                                v-bind:value="index + initialItemLimit + 1" />
                            <slot
                                v-bind="item"
                                name="content"
                                v-bind:display-index="index + initialItemLimit + 1">
                                {{ item }}
                            </slot>
                        </div>
                    </div>
                </div>
            </pc-transition>
            <div v-if="showExpandLink">
                <slot
                    name="footer"
                    v-bind:text="ellipsisText">
                    <pc-button
                        link
                        data-test-role="expand-list-toggle"
                        class="py-0"
                        v-on:click.stop="toggleExpanded">
                        <div class="text-start">
                            {{ ellipsisText }}
                        </div>
                    </pc-button>
                </slot>
            </div>
        </div>
    </div>
</template>
<script>
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/pcComponentDefaults.i18n";

    const defaultInitialItemLimit = 3;
    export default {
        props: {
            items: { type: Array, required: false, default: () => [] },
            keyPropGetter: { type: [String, Function], required: false, default: null },
            initialItemLimit: { type: Number, required: false, default: defaultInitialItemLimit },

            header: { type: String, required: false, default: null },
            hideIfEmpty: { type: Boolean, required: false, default: false },
            emptyMessage: { type: String, required: false, default: null },
            useBullets: { type: Boolean, required: false, default: false },

            /** Set to true to turn the expand-list into a regular list conditionally */
            alwaysExpanded: { type: Boolean, required: false, default: false },
            /** Set to true to show link button when the expand-list is alwaysExpanded **/
            alwaysExpandedShowLink: { type: Boolean, required: false, default: false }
        },
        data() {
            return {
                isExpandedInternal: this.alwaysExpanded
            };
        },
        computed: {
            isExpanded() {
                return this.isExpandedInternal || this.alwaysExpanded;
            },
            internalItems() {
                return this.items || [];
            },
            hasHeader() {
                return this.header != null;
            },
            empty() {
                return this.internalItems.length === 0;
            },
            show() {
                return !this.empty || !this.hideIfEmpty;
            },
            nMoreItems() {
                return this.internalItems.length - this.initialItemLimit;
            },
            expandLinkText() {
                return this.isExpanded ? this.showLessMessage : this.showMoreMessage;
            },
            ellipsisText() {
                return this.hasHeader ? (!this.isExpanded ? "..." : this.showLessMessage) : this.expandLinkText;
            },
            initialItemList() {
                return this.internalItems.slice(0, this.initialItemLimit);
            },
            moreItemList() {
                return this.internalItems.slice(this.initialItemLimit);
            },
            hasMore() {
                return this.nMoreItems > 0;
            },
            showExpandLink() {
                return this.hasMore && (!this.alwaysExpanded || this.alwaysExpandedShowLink);
            },
            emptyMessageDisplay() {
                return this.emptyMessage ?? this.defaultEmptyMessage;
            },
            ...mapI18n(
                {
                    defaultEmptyMessage: KEYS.pcExpandListEmptyMessage,
                    showLessMessage: KEYS.pcExpandListShowLess,
                    showMoreMessage() {
                        return {
                            key: KEYS.pcExpandListShowMore,
                            values: {
                                moreItems: this.nMoreItems
                            }
                        };
                    }
                },
                NAMESPACE
            )
        },
        methods: {
            toggleExpanded() {
                if (this.alwaysExpanded) {
                    return;
                }

                this.isExpandedInternal = !this.isExpandedInternal;
            },
            getItemKey(item) {
                if (this.keyPropGetter == null) {
                    return item;
                }

                if (typeof this.keyPropGetter == "function") {
                    return this.keyPropGetter(item);
                } else if (typeof this.keyPropGetter == "string") {
                    return item[this.keyPropGetter];
                }

                throw "Invalid key prop getter for expandable list";
            }
        }
    };
</script>
